import React, { useState, useEffect } from 'react';
import './SmartInboxEmail.css';
import './EmailPopup.css';
import { database } from '../firebaseConfig';
import { ref, get, update, remove, set } from 'firebase/database';
import parse from 'html-react-parser';
import { Options } from 'html-react-parser';
import DOMPurify from 'dompurify';
import EmailAiPopup from './EmailAiPopup';
import EmailSentNotifier from './EmailSentNotifier';
import SetTagPopup from './SetTagPopup';
import { getFunctions, httpsCallable } from 'firebase/functions';
import quotedPrintable from 'quoted-printable';
import he from 'he';
import ReactDOM from 'react-dom';
import { Letter } from 'react-letter';

const SmartInboxEmail = ({ email, userId, emailType = 'inbox', onDelete, onMarkAsReplied, onProcessWithAgents, autoProcessEnabled }) => {
  const [expanded, setExpanded] = useState(false);
  const [isRead, setIsRead] = useState(email.isRead || false);
  const [isReplied, setIsReplied] = useState(email.isReplied || false);
  const [loading, setLoading] = useState(false);
  const [showEmailAiPopup, setShowEmailAiPopup] = useState(false);
  const [emailReplyData, setEmailReplyData] = useState(null);
  const [hasReplied, setHasReplied] = useState(false);
  const [serverSyncStatus, setServerSyncStatus] = useState('');
  const [emailNotifier, setEmailNotifier] = useState({
    status: '',
    message: ''
  });
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [htmlViewMode, setHtmlViewMode] = useState('iframe'); // 'iframe' oder 'parsed'
  const [processingWithAgents, setProcessingWithAgents] = useState(false);
  const [showForwardEmailInput, setShowForwardEmailInput] = useState(false);
  const [forwardEmailAddress, setForwardEmailAddress] = useState('');
  const [showSetTagPopup, setShowSetTagPopup] = useState(false);

  // Hilfsfunktion für konsistente E-Mail-ID
  const getEmailId = () => {
    return email.id || email.messageId || `temp_${Date.now()}`;
  };

  // Prüfe, ob es sich um eine gesendete E-Mail handelt
  const isSentEmail = email.isSent === true;

  // Setze den Standard-Anzeigemodus basierend auf verfügbaren Daten
  useEffect(() => {
    if (email.htmlStorageUrl) {
      setHtmlViewMode('iframe');
    } else {
      setHtmlViewMode('parsed');
    }
  }, [email.htmlStorageUrl]);

  // Firebase Functions
  const functions = getFunctions();
  const markEmailAsReadFunc = httpsCallable(functions, 'markEmailAsRead');

  // Formatiere das Datum für bessere Lesbarkeit
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      
      // Format the time (HH:MM)
      const time = date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit'
      });
      
      // Format the date (MM/DD/YYYY)
      const formattedDate = date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      
      return { time, date: formattedDate };
    } catch (err) {
      console.error('Error formatting date:', err);
      return { time: '', date: dateString };
    }
  };
  
  // Extrahiere den Namen und die E-Mail-Adresse aus dem Absender/Empfänger
  const parseFromField = (fromString) => {
    if (!fromString) return { name: 'Unknown', email: '', hasName: false };
    
    try {
      // Versuche, Name und E-Mail-Adresse zu extrahieren
      const match = fromString.match(/^(.*?)\s*<(.+?)>$/);
      if (match) {
        const name = match[1].trim();
        return {
          name: name,
          email: match[2].trim(),
          hasName: name.length > 0 // Hat einen Namen, wenn das Namen-Feld nicht leer ist
        };
      }
      
      // Wenn es nur eine E-Mail-Adresse ohne Namen ist
      if (fromString.includes('@')) {
        return {
          name: fromString,
          email: fromString.trim(),
          hasName: false // Keine Name, nur E-Mail-Adresse
        };
      }
      
      // Wenn kein Match und keine E-Mail-Adresse, gib den gesamten String als Name zurück
      return {
        name: fromString,
        email: '',
        hasName: true // Wir betrachten den unbekannten String als Namen
      };
    } catch (err) {
      console.error('Error parsing email address:', err);
      return { name: fromString, email: '', hasName: false };
    }
  };

  const { time, date } = formatDate(email.date);
  
  // Bei gesendeten E-Mails zeigen wir den Empfänger statt des Absenders an
  const fromOrToField = (isSentEmail || emailType === 'sent' || emailType === 'replied' || emailType === 'forwarded') 
    ? (email.to || email.recipients || '') 
    : email.from;
  const displayPerson = parseFromField(fromOrToField);

  // Bei gesendeten E-Mails zusätzlich den Absender parsen
  const senderPerson = (isSentEmail || emailType === 'sent' || emailType === 'replied' || emailType === 'forwarded') 
    ? parseFromField(email.from) 
    : null;

  // Zeige die richtige E-Mail-Adresse im Badge - je nach E-Mail-Typ
  const badgeEmail = isSentEmail 
    ? (email.from ? parseFromField(email.from).email : '')  // Bei gesendeten E-Mails: from
    : (email.to ? parseFromField(email.to).email : (email.accountEmail || ''));  // Bei empfangenen E-Mails: to
  
  const accountEmail = badgeEmail || 'Unknown account';
  
  // Formatiere E-Mail-Adresse für die Anzeige (max. 20 Zeichen)
  const formatShortEmail = (email) => {
    if (!email || email.length <= 20) return email;
    const parts = email.split('@');
    if (parts.length !== 2) return email;
    
    const username = parts[0];
    const domain = parts[1];
    
    // Kürzen des Benutzernamens, wenn zu lang
    const shortUsername = username.length > 10 
      ? username.substring(0, 8) + '...' 
      : username;
    
    // Kürzen der Domain, wenn zu lang
    const shortDomain = domain.length > 8
      ? domain.substring(0, 6) + '...'
      : domain;
    
    return `${shortUsername}@${shortDomain}`;
  };
  
  const displayAccountEmail = formatShortEmail(accountEmail);
  
  // Prüfe, ob die E-Mail aus dem Spam-Ordner stammt
  const isSpam = email.isSpam || false;

  // Begeisterungsgrad-Anzeige
  const getEnthusiasmScore = () => {
    if (email.enthusiasmScore === undefined) {
      return {
        score: null,
        className: 'enthusiasm_score_unknown',
        text: 'No rating'
      };
    }
    
    const score = parseInt(email.enthusiasmScore, 10);
    
    if (score <= 3) {
      return {
        score,
        className: 'enthusiasm_score_negative',
        text: `${score}/10`
      };
    } else if (score <= 7) {
      return {
        score,
        className: 'enthusiasm_score_neutral',
        text: `${score}/10`
      };
    } else {
      return {
        score,
        className: 'enthusiasm_score_positive',
        text: `${score}/10`
      };
    }
  };
  
  const enthusiasmInfo = getEnthusiasmScore();

  // Prüfe beim Laden, ob diese E-Mail bereits beantwortet wurde
  useEffect(() => {
    // Bei gesendeten E-Mails brauchen wir nicht zu prüfen, ob sie beantwortet wurden
    if (isSentEmail) return;
    
    const checkIfReplied = async () => {
      try {
        const emailId = getEmailId();
        
        // Prüfe in der Datenbank, ob es einen Eintrag für diese E-Mail-ID gibt
        const repliesRef = ref(database, `/World/${userId}/emailReplies/${emailId}`);
        const snapshot = await get(repliesRef);
        
        if (snapshot.exists()) {
          setHasReplied(true);
          // Für gelöschte E-Mails: Nur lokalen Status aktualisieren, keine DB-Updates
          if (emailType === 'deleted') {
            setIsReplied(true); // Nur lokaler Status für gelöschte E-Mails
            console.log(`🗑️ [DELETED] Skipping onMarkAsReplied for deleted email ${emailId}`);
          } else {
            // Wenn die E-Mail in der DB als beantwortet markiert ist, aber nicht im Objekt
            if (!email.isReplied && onMarkAsReplied) {
              setIsReplied(true); // Lokalen Status aktualisieren
              // Server-Status nachträglich aktualisieren
              onMarkAsReplied(email);
            }
          }
        }
      } catch (error) {
        console.error("Error checking reply status:", error);
      }
    };
    
    if (email && getEmailId()) {
      checkIfReplied();
    }
  }, [email, userId, isSentEmail, onMarkAsReplied, emailType]);

  // Eine Funktion, die aufgerufen wird, wenn auf die E-Mail geklickt wird
  const toggleExpand = async () => {
    const wasExpanded = expanded;
    setExpanded(!wasExpanded);
    
    // Gesendete E-Mails sind immer als gelesen markiert
    if (isSentEmail) return;
    
    // Nur als gelesen markieren, wenn die E-Mail noch nicht als gelesen markiert ist
    if (!wasExpanded && !isRead) {
      console.log(`Marking email "${email.subject}" as read`);
      
      const emailId = getEmailId();
      setIsRead(true);
      
      try {
        // Aktualisiere den Status in der Datenbank und auf dem Server
        const result = await markEmailAsReadFunc({ emailId: emailId });
        
        // Überprüfe, ob die E-Mail auch auf dem Server als gelesen markiert wurde
        if (result.data.serverUpdated) {
          console.log("Email successfully marked as read on the server");
          setServerSyncStatus('synced');
        } else if (result.data.error) {
          console.warn("Email could not be updated on server:", result.data.error);
          setServerSyncStatus('error');
        } else {
          console.log("Email only updated locally");
          setServerSyncStatus('local');
        }
      } catch (error) {
        console.error("Error marking as read:", error);
        setServerSyncStatus('error');
      }
    }
  };

  // Funktion zum Soft-Delete der E-Mail (markiert als deleted)
  const handleDelete = async () => {
    const emailId = getEmailId();
    if (!userId || !email || !emailId) return;
    
    try {
      setDeleting(true);
      
      // Alle E-Mails bleiben in /inbox und bekommen nur den deleted Status
      // Bestimme den korrekten Pfad basierend auf der E-Mail-Quelle
      let emailRef;
      
      if (email.isSpam || email.source === 'spam') {
        // Spam-E-Mails: Status in /spam setzen
        emailRef = ref(database, `/World/${userId}/spam/${emailId}`);
      } else {
        // Alle anderen E-Mails: Status in /inbox setzen (empfangen und gesendet)
        emailRef = ref(database, `/World/${userId}/inbox/${emailId}`);
      }
      
      await update(emailRef, {
        deleted: true,
        deletedAt: Date.now()
      });
      
      console.log(`Email ${emailId} marked as deleted in ${email.isSpam || email.source === 'spam' ? 'spam' : 'inbox'}`);
      
      // Rufe onDelete auf, damit die übergeordnete Komponente die E-Mail aus der Anzeige entfernt
      if (onDelete) {
        onDelete(email);
      }
      
    } catch (error) {
      console.error('Error marking email as deleted:', error);
      setError(`Error deleting email: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Funktion zum Wiederherstellen der E-Mail (entfernt deleted Status)
  const handleRecover = async () => {
    const emailId = getEmailId();
    if (!userId || !email || !emailId) return;
    
    try {
      setDeleting(true);
      
      // Bestimme den korrekten Pfad basierend auf der E-Mail-Quelle
      let emailRef;
      
      if (email.isSpam || email.deletionSource === 'spam') {
        // Spam-E-Mails: Status in /spam entfernen
        emailRef = ref(database, `/World/${userId}/spam/${emailId}`);
      } else {
        // Alle anderen E-Mails: Status in /inbox entfernen
        emailRef = ref(database, `/World/${userId}/inbox/${emailId}`);
      }
      
      // Entferne die deleted und deletedAt Felder
      await update(emailRef, {
        deleted: null,
        deletedAt: null
      });
      
      console.log(`Email ${emailId} recovered from deleted status in ${email.isSpam || email.deletionSource === 'spam' ? 'spam' : 'inbox'}`);
      
      // Rufe onDelete auf, damit die übergeordnete Komponente die E-Mail aus der Deleted-Anzeige entfernt
      if (onDelete) {
        onDelete(email);
      }
      
    } catch (error) {
      console.error('Error recovering email:', error);
      setError(`Error recovering email: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Funktion zum Antworten auf eine E-Mail (nicht für gesendete E-Mails)
  const handleReplyWithAi = async () => {
    if (!email || isSentEmail) return;
    
    // Prüfe, ob die E-Mail Tags hat und ob einer davon ein vorausgewähltes Topic hat
    let selectedTopic = "";
    if (email.tags && Array.isArray(email.tags)) {
      // Lade die AI Tag Definitionen
      const aiTagsRef = ref(database, `/World/${userId}/settings/aiTags`);
      const snapshot = await get(aiTagsRef);
      
      if (snapshot.exists()) {
        const aiTags = [];
        snapshot.forEach((childSnapshot) => {
          aiTags.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        
        // Suche nach einem Tag mit selectedTopic
        for (const tag of email.tags) {
          const tagDefinition = aiTags.find(aiTag => aiTag.tag === tag);
          if (tagDefinition && tagDefinition.selectedTopic) {
            selectedTopic = tagDefinition.selectedTopic;
            break;
          }
        }
      }
    }
    
    // Erstelle ein Objekt mit den Daten, die EmailAiPopup benötigt
    const replyData = {
      id: getEmailId(),
      business_name: displayPerson.name,
      lead_email: displayPerson.email,
      lead_description: email.bodyText || email.preview || "",
      // Füge weitere Felder hinzu, die EmailAiPopup erwartet
      lead_website: "",
      lead_instagram: "",
      lead_tiktok: "",
      lead_type: "email_reply",
      // Speichere die ursprüngliche E-Mail für den Kontext
      original_email: {
        subject: email.subject,
        content: email.bodyText || email.preview || "",
        from: email.from,
        date: email.date
      },
      // Füge die Konto-Informationen hinzu
      accountEmail: email.accountEmail,
      accountId: email.accountId,
      // Füge das vorausgewählte Topic hinzu
      selectedTopic: selectedTopic
    };
    
    setEmailReplyData(replyData);
    setShowEmailAiPopup(true);
  };

  // Funktion zum Weiterleiten einer E-Mail an eine neue Adresse
  const handleForwardWithAi = async () => {
    if (!email) return;
    
    // Zeige zuerst das Eingabefeld für die E-Mail-Adresse
    setShowForwardEmailInput(true);
  };

  // Funktion zum Bestätigen der Forward-E-Mail-Adresse
  const handleForwardEmailSubmit = () => {
    if (!forwardEmailAddress.trim()) {
      setEmailNotifier({
        status: 'error',
        message: 'Please enter an email address'
      });
      setTimeout(() => resetEmailNotifier(), 3000);
      return;
    }

    // Verbesserte E-Mail-Validierung
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!emailRegex.test(forwardEmailAddress.trim())) {
      setEmailNotifier({
        status: 'error',
        message: 'Please enter a valid email address'
      });
      setTimeout(() => resetEmailNotifier(), 3000);
      return;
    }
    
    // Erstelle den E-Mail-Verlauf für die Weiterleitung als fertigen Content
    let emailBodyContent = email.bodyText || email.preview || email.content || 'No content available';
    
    // Begrenze den Content auf 10000 Zeichen um Database-Limits zu vermeiden
    if (emailBodyContent.length > 10000) {
      emailBodyContent = emailBodyContent.substring(0, 10000) + '\n\n[Content truncated due to length...]';
    }
    
    const forwardedEmailContent = `
--- Forwarded Email ---
From: ${email.from || 'Unknown'}
To: ${email.to || email.accountEmail || 'Unknown'}
Date: ${email.date || 'Unknown'}
Subject: ${email.subject || '(No Subject)'}

${emailBodyContent}
--- End of Forwarded Email ---
    `.trim();
    
    // Verbesserte Temp-ID Generierung mit besserer Eindeutigkeit
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substr(2, 9);
    const emailId = getEmailId() || 'unknown';
    const tempId = `temp_forward_${emailId}_${timestamp}_${randomSuffix}`;
    
    // Erstelle ein Objekt mit den Daten für die Weiterleitung
    const forwardData = {
      id: tempId,
      business_name: "", // Leer lassen für neue Eingabe
      lead_email: forwardEmailAddress.trim(), // Die eingegebene E-Mail-Adresse
      lead_description: "", // Leer lassen, da wir den Content direkt setzen
      // Weitere Felder
      lead_website: "",
      lead_instagram: "",
      lead_tiktok: "",
      lead_type: "email_forward",
      // Flag für Forward-Erkennung
      isForwardOperation: true,
      // Ursprüngliche E-Mail für Kontext
      original_email: {
        subject: `Fwd: ${email.subject || '(No Subject)'}`,
        content: forwardedEmailContent,
        from: email.accountEmail || email.from || 'Unknown', // Absender ist das Account das weiterleitet
        to: forwardEmailAddress.trim(), // Empfänger ist die neue Forward-Adresse
        date: new Date().toISOString(), // Aktuelles Datum für die Weiterleitung
        // Zusätzliche Felder für vollständige Kompatibilität
        recipients: forwardEmailAddress.trim(),
        accountEmail: email.accountEmail,
        isForward: true,
        originalEmailId: getEmailId() // Referenz zur ursprünglichen E-Mail
      },
      // Konto-Informationen vom aktuellen Benutzer
      accountEmail: email.accountEmail,
      accountId: email.accountId,
      // Überschreibe die "to" Adresse für die Weiterleitung
      to: forwardEmailAddress.trim(),
      recipients: forwardEmailAddress.trim(),
      // Kein selectedTopic für Forward
      selectedTopic: "",
      // WICHTIG: Setze den E-Mail-Content direkt als Body-Content
      prefilledBodyContent: forwardedEmailContent,
      skipGeneration: true // Flag um automatische Generierung zu überspringen
    };
    
    // Schließe das E-Mail-Input und öffne das EmailAiPopup
    setShowForwardEmailInput(false);
    setForwardEmailAddress('');
    
    // Debug: Logge die Forward-Daten
    console.log('🔄 [FORWARD] Forward data being sent to EmailAiPopup:', forwardData);
    console.log('🔄 [FORWARD] Forward email address:', forwardEmailAddress.trim());
    console.log('🔄 [FORWARD] Prefilled body content length:', forwardedEmailContent.length);
    
    setEmailReplyData(forwardData);
    setShowEmailAiPopup(true);
  };

  // Funktion zum Abbrechen der Forward-Eingabe
  const handleForwardEmailCancel = () => {
    setShowForwardEmailInput(false);
    setForwardEmailAddress('');
  };

  // Funktion zum Speichern des Antwortstatus in der Datenbank
  const saveReplyStatus = async (emailId, saveToDatabase = true) => {
    try {
      // Lokalen Status aktualisieren
      setHasReplied(true);
      setIsReplied(true);
      
      // Nur in der Datenbank speichern wenn explizit gewünscht
      if (saveToDatabase) {
        // Prüfe zuerst, ob bereits ein Reply-Eintrag existiert
        const repliesRef = ref(database, `/World/${userId}/emailReplies/${emailId}`);
        const existingSnapshot = await get(repliesRef);
        
        let replyData;
        if (existingSnapshot.exists()) {
          // Verwende den existierenden Timestamp, um Änderungen zu vermeiden
          const existingData = existingSnapshot.val();
          replyData = {
            timestamp: existingData.timestamp || Date.now(), // Behalte existierenden Timestamp
            emailId: emailId,
            responded: true
          };
          console.log(`🟡 [DB UPDATE] Updating existing reply status for email ${emailId} (keeping original timestamp)`);
        } else {
          // Neuer Eintrag - setze neuen Timestamp
          replyData = {
            timestamp: Date.now(),
            emailId: emailId,
            responded: true
          };
          console.log(`🔵 [DB WRITE] Creating new reply status for email ${emailId}`);
        }
        
        await set(repliesRef, replyData);
        console.log(`Reply status saved for email ${emailId}`);
      } else {
        console.log(`🟡 [DB SKIP] Reply status update skipped for email ${emailId} - saveToDatabase=false`);
      }
      
      // Server-Status aktualisieren via onMarkAsReplied
      if (onMarkAsReplied && typeof onMarkAsReplied === 'function') {
        onMarkAsReplied(email);
      }
      
      return true;
    } catch (error) {
      console.error("Error saving reply status:", error);
      return false;
    }
  };

  // Callback für das Schließen des EmailAiPopup
  const handleEmailPopupClose = async (emailSent = false) => {
    // Schließe sofort das Popup
    setShowEmailAiPopup(false);
    
    // Prüfe explizit, ob eine E-Mail gesendet wurde (strikte Gleichheit)
    if (emailSent === true) {
      console.log("E-Mail sent successfully!");
      
      // Zeige die Erfolgsmeldung
      setEmailNotifier({
        status: 'success',
        message: 'E-Mail sent successfully!'
      });
      
      // Notifier nach 5 Sekunden automatisch ausblenden
      setTimeout(() => {
        resetEmailNotifier();
      }, 5000);
      
      // 🔥 FIX: Nur bei echten Antworten (nicht bei Forward) Reply-Status aktualisieren
      if (emailReplyData && !emailReplyData.isForwardOperation) {
        const emailId = getEmailId();
        await saveReplyStatus(emailId);
      } else {
        console.log("🔄 [FORWARD-INFO] Skipping reply status update for forward operation");
      }
    } else {
      console.log("EmailAiPopup wurde geschlossen ohne E-Mail zu senden");
    }
  };

  // Funktion zum Zurücksetzen des Notifiers
  const resetEmailNotifier = () => {
    setEmailNotifier({ 
      status: '', 
      message: '', 
      scheduledTime: null, 
      recipientEmail: null 
    });
  };

  // Funktion zum Behandeln des E-Mail-Ergebnisses - direkt vom EmailAiPopup aufgerufen
  const handleEmailResult = (status, message, rowData, scheduledTime, recipientEmail) => {
    console.log(`E-Mail-Ergebnis empfangen: ${status} - ${message}`, {
      scheduledTime: scheduledTime ? new Date(scheduledTime).toLocaleString() : null,
      recipientEmail: recipientEmail || 'nicht angegeben',
      isForward: rowData?.isForwardOperation || false
    });
    
    // Stelle sicher, dass scheduledTime ein Zeitstempel ist
    let validScheduledTime = scheduledTime;
    if (scheduledTime && typeof scheduledTime === 'string') {
      try {
        validScheduledTime = new Date(scheduledTime).getTime();
        if (isNaN(validScheduledTime)) {
          console.error("Ungültiger Zeitstempel:", scheduledTime);
          validScheduledTime = null;
        }
      } catch (e) {
        console.error("Fehler beim Konvertieren des Zeitstempels:", e);
        validScheduledTime = null;
      }
    }
    
    // 🔄 [FORWARD-INFO] Spezielle Behandlung für Forward-E-Mails
    if (rowData?.isForwardOperation && status === 'success') {
      console.log("🔄 [FORWARD-SUCCESS] Forward-E-Mail erfolgreich gesendet");
    }
    
    setEmailNotifier({ 
      status, 
      message, 
      scheduledTime: validScheduledTime,
      recipientEmail: recipientEmail || (rowData ? rowData.lead_email : null)
    });
    
    // Automatisch nach 5 Sekunden ausblenden
    setTimeout(() => {
      resetEmailNotifier();
    }, 5000);
  };

  // Funktion zur sicheren Anzeige des E-Mail-Inhalts mit React Letter
  const renderEmailContent = () => {
    // Wenn keine Inhalte verfügbar sind
    if (!email.bodyHtml && !email.bodyText && !email.content && !email.text && !email.htmlStorageUrl) {
      return <p>No content available</p>;
    }

    // Bestimme HTML und Text Content
    const htmlContent = email.bodyHtml || email.content || '';
    const textContent = email.bodyText || email.text || '';

    return (
      <div className="email-content-safe-container">
        {/* Action Buttons */}
        <div className="email-view-mode-toggle">
          <div className="email-view-toggle-info">
            Email
          </div>
          <div className="email-view-toggle-actions">
            {/* Process Button - ganz links */}
            {!isSentEmail && onProcessWithAgents && (
              <button 
                className={`inbox-email-process-action-btn ${processingWithAgents ? 'processing' : ''} ${email.processedByAgent ? 'processed' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleProcessWithAgents();
                }}
                disabled={processingWithAgents}
                title={email.processedByAgent ? 'Already processed by agent' : 'Process with AI agents'}
              >
                {processingWithAgents ? (
                  <>
                    Processing...
                    <span className="process_spinner"></span>
                  </>
                ) : email.processedByAgent ? (
                  <>
                    Reply Generated
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </>
                ) : (
                  <>
                    Auto Reply
                  <img src="/img/ai_stars.svg" alt="Process" className="process_icon" style={{width: '18px', height: '18px', filter: 'invert(1)'}}/>
                  </>
                )}
              </button>
            )}
            
            {/* Forward Button - mitte (für alle E-Mail-Typen) */}
            <button 
              className="inbox-email-forward-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleForwardWithAi();
              }}
              title="Forward with AI"
            >
              Forward with AI
             <img src="/img/reply-message.svg" alt="Forward" className="forward_icon" style={{width: '17px', height: '16px', filter: 'invert(1)'}}/>
            </button>
            
            {/* Reply Button - ganz rechts (nur für empfangene E-Mails) */}
            {!isSentEmail && (
              <button 
                className="inbox-email-reply-action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReplyWithAi();
                }}
                title="Reply with AI"
              >
                {hasReplied ? 'Reply Again' : 'Reply Email'}
                <img src="/img/paperplane.svg" alt="Reply" className="reply_icon" />
              </button>
            )}
          </div>
        </div>

        {/* Email Content mit React Letter */}
        <div className="email-content-body">
          {htmlViewMode === 'iframe' && email.htmlStorageUrl ? (
            // Original iframe view (fallback)
            <iframe
              src={email.htmlStorageUrl}
              className="email-content-iframe"
              title="Email Content"
              sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              style={{
                width: '100%',
                minHeight: '400px',
                border: 'none',
                padding: '10px',
                borderRadius: '12px',
                backgroundColor: '#fff',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }}
            />
          ) : (
            // Sichere React Letter Anzeige
            <div className="email-content-react-letter">
              <Letter 
                html={htmlContent}
                text={textContent}
                useIframe={false} // Wir verwenden kein iframe für bessere Integration
                iframeTitle={email.subject}
                rewriteExternalLinks={(url) => {
                  // Privacy-Protection: Externe Links über Proxy leiten
                  console.log('Rewriting external link:', url);
                  return `/api/proxy-link?url=${encodeURIComponent(url)}`;
                }}
                rewriteExternalResources={(url) => {
                  // Bilder und Ressourcen über Proxy laden
                  console.log('Rewriting external resource:', url);
                  return `/api/proxy-image?url=${encodeURIComponent(url)}`;
                }}
                allowedSchemas={['http', 'https', 'mailto']}
                preserveCssPriority={true}
                className="safe-email-content"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  // Rendere zusätzliche Informationen für gesendete E-Mails
  const renderSentEmailInfo = () => {
    if (!isSentEmail) return null;
    
    return (
      <div className="smart_email_sent_details">
        {email.delay && (
          <div className="smart_email_sent_detail_item">
            <span className="smart_email_sent_detail_label">Verzögerung:</span>
            <span className="smart_email_sent_detail_value">
              {email.delay} Minuten ({Math.round(email.delay / 60 * 10) / 10} Stunden)
            </span>
          </div>
        )}
        
        {email.randomizationFactor !== undefined && (
          <div className="smart_email_sent_detail_item">
            <span className="smart_email_sent_detail_label">Zufallsfaktor:</span>
            <span className="smart_email_sent_detail_value">
              {Math.round(email.randomizationFactor * 100)}%
            </span>
          </div>
        )}
        
        {email.scheduledTime && (
          <div className="smart_email_sent_detail_item">
            <span className="smart_email_sent_detail_label">Geplant für:</span>
            <span className="smart_email_sent_detail_value">
              {new Date(email.scheduledTime).toLocaleString()}
            </span>
          </div>
        )}
        
        {email.actualSendTime && (
          <div className="smart_email_sent_detail_item">
            <span className="smart_email_sent_detail_label">Tatsächlich gesendet:</span>
            <span className="smart_email_sent_detail_value">
              {new Date(email.actualSendTime).toLocaleString()}
            </span>
          </div>
        )}
        
        {email.emailType && (
          <div className="smart_email_sent_detail_item">
            <span className="smart_email_sent_detail_label">E-Mail-Typ:</span>
            <span className="smart_email_sent_detail_value">
              {email.emailType}
            </span>
          </div>
        )}
      </div>
    );
  };

  // Füge eine Funktion zum Rendern des Antwort-Status-Indikators hinzu
  const renderReplyStatus = () => {
    if (isSentEmail) return null; // Kein Antwort-Status für gesendete E-Mails
    
    const replied = isReplied || hasReplied || email.isReplied;
    
    if (!replied) return null;
    
    return (
      <div className="email_replied_indicator" title="Replied">
        <img src="/img/reply-message.svg" alt="Replied" className="replied_icon" />
      </div>
    );
  };

  // Stelle sicher, dass wir die richtige CSS-Klasse basierend auf dem Lese-Status und Spam-Status haben
  const emailClassName = `smartInbox_email ${expanded ? 'expanded' : ''} ${
    !isRead ? 'unread' : ''
  } ${isSpam ? 'spam' : ''} ${isReplied || hasReplied || email.isReplied ? 'replied' : ''} ${isSentEmail ? 'sent' : ''}`;

  // Render-Methode für das EmailAiPopup als Portal
  const renderEmailPopup = () => {
    if (showEmailAiPopup && emailReplyData) {
      return ReactDOM.createPortal(
        <div className="popupOverlayWrapper">
          <div className="popupOverlayBackground"></div>
          <div className="popupContentWrapper">
            <EmailAiPopup 
              onClose={(emailSent) => handleEmailPopupClose(emailSent)} 
              rowData={emailReplyData} 
              userId={userId}
              isReply={true}
              source="inbox"
              onEmailResult={handleEmailResult}
            />
          </div>
        </div>,
        document.body // Rendere direkt im body-Element
      );
    }
    return null;
  };

  // Funktion zum Verarbeiten mit Agents
  const handleProcessWithAgents = async () => {
    if (!onProcessWithAgents || processingWithAgents) return;
    
    const emailId = getEmailId();
    if (!emailId) return;

    setProcessingWithAgents(true);
    try {
      await onProcessWithAgents(emailId, email.isSpam || false);
    } catch (error) {
      console.error('Error processing with agents:', error);
    } finally {
      setProcessingWithAgents(false);
    }
  };

  // Tag-Management Funktionen
  const handleSetTagClick = (e) => {
    e.stopPropagation();
    setShowSetTagPopup(true);
  };

  const handleSetTagPopupClose = () => {
    setShowSetTagPopup(false);
  };

  const handleTagsUpdated = (emailId, updatedTags) => {
    // Aktualisiere die lokalen Tags
    if (email) {
      email.tags = updatedTags;
    }
    console.log(`Tags updated for email ${emailId}:`, updatedTags);
  };

  // Prüfe, ob E-Mail Tags hat
  const hasEmailTags = () => {
    if (!email.tags) return false;
    
    const tagsArray = Array.isArray(email.tags) 
      ? email.tags 
      : Object.values(email.tags || {});
    
    return tagsArray.length > 0;
  };

  // Hol das erste Tag-Icon für den Button
  const getFirstTagIcon = () => {
    if (!hasEmailTags()) return null;
    
    const tagsArray = Array.isArray(email.tags) 
      ? email.tags 
      : Object.values(email.tags || {});
    
    const firstTag = tagsArray[0];
    if (typeof firstTag === 'string') return null;
    
    return firstTag.icon || null;
  };

  return (
    <div 
      className={`smartInbox_email ${isRead ? '' : 'unread'} ${expanded ? 'expanded' : ''} ${isReplied ? 'replied' : ''} ${isSentEmail ? 'sent' : ''} ${emailType === 'replied' ? 'replied' : ''} ${emailType === 'forwarded' ? 'forwarded' : ''}`}
    >
      <div className="smartInbox_email_header" onClick={toggleExpand}>
      
        <div className="smartInbox_email_left">
        {(emailType === 'sent' || emailType === 'replied' || emailType === 'forwarded') && (
          <div className="smartInbox_email_direction_container">
            <div className={`smartInbox_email_direction smartInbox_email_direction_${emailType}`}>
              {emailType === 'sent' && 'Sent to:'}
              {emailType === 'replied' && 'Replied to:'}
              {emailType === 'forwarded' && 'Forwarded to:'}
            </div>
            {emailType === 'sent' && email.emailType && (
              <div className={`smartInbox_email_type_tag ${email.emailType || 'default'}`}>
                {email.emailType}
              </div>
            )}
            {/* Status Anzeige */}
            {(emailType === 'sent' || emailType === 'replied' || emailType === 'forwarded') && (
              <div className={`smartInbox_email_status_tag smartInbox_email_status_${emailType}`}>
                {emailType === 'forwarded' ? (
                  // Für Forwarded: zeige "sent" Status (true/false)
                  email.sent ? 'Sent' : 'Not Sent'
                ) : (
                  // Für Sent und Replied: zeige "status" Wert
                  email.status || 'Unknown'
                )}
              </div>
            )}
          </div>
        )}
          {displayPerson.hasName && displayPerson.email && displayPerson.name !== displayPerson.email && (
            <span className="smartInbox_email_address" title={displayPerson.email}>
              {displayPerson.email}
            </span>
          )}
          <div className="smartInbox_email_sender">
            <span className="smartInbox_email_name">
              {displayPerson.name || 'Unknown'}
            </span>
            
            {/* Zeige Begeisterungsgrad an, wenn vorhanden und nicht gesendete E-Mail */}
            {!isSentEmail && enthusiasmInfo.score !== null && (
              <span className={`enthusiasm_score ${enthusiasmInfo.className}`} title="Enthusiasm Score">
                {enthusiasmInfo.text}
              </span>
            )}
            
            {/* Zeige E-Mail-Topic an, wenn vorhanden und nicht gesendete E-Mail */}
            {!isSentEmail && email.emailTopic && (
              <span className="email_topic" title="Email Topic">
                {email.emailTopic}
              </span>
            )}
            
            {/* Zeige Tags an, wenn vorhanden */}
            {email.tags && (
              <div className="smartInbox_email_tags">
                {(() => {
                  // Konvertiere Tags zu Array falls sie als Object gespeichert sind
                  const tagsArray = Array.isArray(email.tags) 
                    ? email.tags 
                    : Object.values(email.tags || {});
                  
                  return tagsArray.map((tag, index) => {
                    // Unterstütze sowohl neue Object-Struktur als auch alte Array-Struktur
                    const tagData = typeof tag === 'string' 
                      ? { tag: tag, name: tag } 
                      : tag;
                    
                    return (
                      <span 
                        key={index} 
                        className="smartInbox_email_tag"
                        style={{
                          backgroundColor: tagData.color || '#666',
                          color: '#ffffff' // Weiße Schrift wie gewünscht
                        }}
                      >
                        {tagData.icon && (
                          <img 
                            src={tagData.icon} 
                            alt=""
                            className="email_tag_icon"
                          />
                        )}
                        <span>{tagData.tag || tagData.name}</span>
                      </span>
                    );
                  });
                })()}
              </div>
            )}
            
            {/* Zeige "Spam"-Indikator an, wenn es sich um Spam handelt */}
            {isSpam && (
              <span className="smartInbox_email_spam_indicator">
                Spam
              </span>
            )}
            
            {/* Beantwortet-Status */}
            {renderReplyStatus()}
            
            {/* Original-Ansicht verfügbar Indikator */}
            {email.htmlStorageUrl && (
              <span className="email_original_view_indicator" title="Original view available">
              </span>
            )}
          </div>
          <div className="smartInbox_email_subject">
            {email.subject || '(No Subject)'}
          </div>
        </div>
        <div className="smartInbox_email_right">
          <div className="smartInbox_email_date">
            <div className="smartInbox_email_time">{time}</div>
            <div className="smartInbox_email_day">{date}</div>
          </div>
          {accountEmail && (
            <div className="smartInbox_email_account" title={`${isSentEmail ? 'Sent via' : 'Received via'} ${accountEmail}`}>
              <span className="smartInbox_email_account_icon">{isSentEmail ? '📤' : '📧'}</span>
              <span className="smartInbox_email_account_label">{displayAccountEmail}</span>
            </div>
          )}
        </div>
        
        {/* Hover-Lösch-Button */}
        <div className="smartInbox_email_hover_actions">
          {emailType === 'deleted' ? (
            /* Recover-Button für gelöschte E-Mails */
            <button 
              className="smartInbox_email_hover_recover"
              onClick={(e) => {
                e.stopPropagation(); // Verhindert das Öffnen der E-Mail
                handleRecover();
              }}
              disabled={deleting}
              title="Recover Email"
            >
              {deleting ? (
                <span className="delete_spinner"></span>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          ) : (
            /* Normaler Delete-Button für nicht-gelöschte E-Mails */
            <button 
              className="smartInbox_email_hover_delete"
              onClick={(e) => {
                e.stopPropagation(); // Verhindert das Öffnen der E-Mail
                handleDelete();
              }}
              disabled={deleting}
              title="Delete"
            >
              {deleting ? (
                <span className="delete_spinner"></span>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                </svg>
              )}
            </button>
          )}
          
          {/* Tag-Management-Button - nur anzeigen wenn Tags vorhanden */}
          {hasEmailTags() && (
            <button 
              className="smartInbox_email_hover_tag"
              onClick={handleSetTagClick}
              title="Manage Tags"
            >
              {getFirstTagIcon() ? (
                <img 
                  src={getFirstTagIcon()} 
                  alt="Tag" 
                  className="tag_icon"
                />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82zM7 7a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" fill="currentColor"/>
                </svg>
              )}
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="smart_email_expanded_content">
          {/* Aktionsbuttons in der oberen rechten Ecke */}
          <div className="smart_email_actions">
            {/* Auto-Processing Status Indicator */}
            {!isSentEmail && autoProcessEnabled && !email.processedByAgent && (
              <div className="smart_email_auto_process_indicator" title="Auto-processing is enabled for new emails">
                <span className="auto_process_icon">⚡</span>
                <span className="auto_process_text">Auto-processing enabled</span>
              </div>
            )}
            
            {/* Löschen-Button für alle E-Mail-Typen anzeigen */}
            {emailType === 'deleted' ? (
              <button 
                className="smart_email_recover_button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRecover();
                }}
                disabled={deleting}
              >
                {deleting ? 'Recovering...' : 'Recover Email'}
              </button>
            ) : (
              <button 
                className="smart_email_delete_button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete();
                }}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
          
          {/* Zusätzliche Informationen für gesendete E-Mails */}
          {renderSentEmailInfo()}
          
          {loading ? (
            <div className="email-loading-animation">
              <span className="loading-dot"></span>
              <span className="loading-dot"></span>
              <span className="loading-dot"></span>
              <span className="loading-dot"></span>
              <p className="loading-message"></p>
            </div>
          ) : (
            <div className="smart_email_body">
              {renderEmailContent()}
            </div>
          )}
          
          {/* Zeige Anhänge an, falls vorhanden */}
          {email.attachments && email.attachments.length > 0 && (
            <div className="smart_email_attachments">
              <h4>Attachments:</h4>
              <div className="smart_email_attachment_list">
                {email.attachments.map((attachment, index) => (
                  <div key={index} className="smart_email_attachment_item">
                    <span className="smart_email_attachment_icon">📎</span>
                    <span className="smart_email_attachment_name">{attachment.filename}</span>
                    <span className="smart_email_attachment_size">
                      ({Math.round(attachment.size / 1024)}KB)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* EmailAiPopup über Portal rendern */}
      {renderEmailPopup()}
      
      {/* Forward E-Mail-Eingabe Modal */}
      {showForwardEmailInput && ReactDOM.createPortal(
        <div className="popupOverlayWrapper">
          <div className="popupOverlayBackground" onClick={handleForwardEmailCancel}></div>
          <div className="forwardEmailInputModal">
            <div className="forwardEmailInputHeader">
              <h3>Forward Email</h3>
              <button 
                className="forwardEmailInputClose" 
                onClick={handleForwardEmailCancel}
                title="Close"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="forwardEmailInputBody">
              <label htmlFor="forwardEmailAddress">Enter recipient email address:</label>
              <input
                id="forwardEmailAddress"
                type="email"
                placeholder="recipient@example.com"
                value={forwardEmailAddress}
                onChange={(e) => setForwardEmailAddress(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleForwardEmailSubmit();
                  }
                  if (e.key === 'Escape') {
                    handleForwardEmailCancel();
                  }
                }}
                autoFocus
                className="forwardEmailInput"
              />
            </div>
            <div className="forwardEmailInputActions">
              <button 
                className="forwardEmailCancelBtn" 
                onClick={handleForwardEmailCancel}
              >
                Cancel
              </button>
              <button 
                className="forwardEmailSubmitBtn" 
                onClick={handleForwardEmailSubmit}
              >
                Continue
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* E-Mail-Benachrichtigung */}
      <EmailSentNotifier 
        status={emailNotifier.status}
        message={emailNotifier.message}
        onClose={resetEmailNotifier}
      />
      
      {/* SetTagPopup */}
      {showSetTagPopup && ReactDOM.createPortal(
        <SetTagPopup 
          email={email}
          userId={userId}
          onClose={handleSetTagPopupClose}
          onTagsUpdated={handleTagsUpdated}
        />,
        document.body
      )}
    </div>
  );
};

export default SmartInboxEmail; 